import {
  FileText,
  ShieldCheck,
  Cpu,
  GitCompare,
  FileCheck,   // Para Confirmación
  Truck,       // Para Aviso de Expedición
  PackageCheck, // Para Recepción de mercancía
  ClipboardList, // Para HES
  Database,     // Para HES - ERP
  Coins,       // Para Facturas
  Receipt,     // Para Estatus de Facturas
  FileSearch,  // Para Buscar Facturas
  FlaskConical, // Para Casos de Prueba
  FileCode2 // Para Especificaciones Técnicas
} from 'lucide-react';
import React, { useEffect } from 'react';
import { useProject } from '../../context/ProjectContext.tsx';
import IntegrationSection from '../Proyectos/components/IntegrationSection.tsx';
import GuiaIntegracionCard from '../Proyectos/components/GuiaIntegracionCard.tsx';
import ArrangementsSection from '../Proyectos/components/ArrangementsSection.tsx';
import { esErpCloud } from '../views/AribaCommerce/Integracion/useGeneralConfig.ts';
import { useAllChannelsProgress, ChannelProgress } from '../../hooks/useAllChannelsProgress.ts';
import { useCasosPruebaProgress, CasoPruebaProgress } from '../../hooks/useCasosPruebaProgress.ts';
import ChannelProgressBadge from '../../components/common/ChannelProgressBadge.tsx';
import ChannelProgressBar from '../../components/common/ChannelProgressBar.tsx';

interface Props {
  onSelect: (option: string) => void;
}

// Desactivado temporalmente a pedido del usuario (2026-07-28) — reactivar
// cambiando esto a `true` cuando la funcionalidad esté lista. Gatea también
// el ítem del sidebar y la ruta en HomeCA.tsx (importan esta constante).
export const FLOW_EXTENSION_API_HABILITADO = false;

export default function CommerceHome({ onSelect }: Props) {
  const { project, projectId } = useProject();
  const { loading: progressLoading, progress: channelsProgress } = useAllChannelsProgress(projectId);
  const { loading: testProgressLoading, progress: casosPruebaProgress } = useCasosPruebaProgress(projectId);
  // Sub-fase K7: en Cloud, los 9 canales ABAP (IDoc/CIG clásico) se
  // reemplazan por la sección de Communication Arrangements — Cloud no usa
  // ese modelo (ver decisión cerrada en Fase 0 de K3). Los datos ABAP que
  // ya existan en un proyecto Cloud (legado) NO se borran ni se ocultan del
  // todo: siguen accesibles navegando directo a la ruta de cada canal,
  // simplemente ya no se promocionan acá como el camino a seguir.
  const esCloud = esErpCloud(project?.erpTipo);

  useEffect(() => {
    // Mismo sufijo de ambiente que el título base (ver public/index.html /
    // .env.development / .env.production) — así la pestaña sigue diciendo
    // "... - QAS" en QAS aunque esta vista lo pise mientras está montada.
    const envLabel = process.env.REACT_APP_ENV_LABEL || '';
    document.title = `OneHub${envLabel} | Commerce`;

    return () => {
      document.title = `OneHub${envLabel}`;
    };
  }, []);

  return (
    <div className="p-8 text-white space-y-10">

      {/* 🧠 Sección: Qué es */}
      <div>
        <h2 className="text-2xl font-semibold mb-3">
          ¿Qué es Commerce Automation?
        </h2>
        <p className="text-gray-300 max-w-2xl">
          Automatiza la integración entre SAP Ariba y tu ERP para el manejo de documentos
          como facturas, órdenes de compra y estados. Optimiza procesos y reduce errores manuales.
        </p>
      </div>

      {/* 🚀 Funcionalidades */}
      <div>
        <h3 className="text-xl mb-4 font-medium text-gray-200">Funcionalidades</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FeatureCard
            icon={<FileText />}
            title="Envío Estatus Facturas"
            description="Envía el estado de facturas hacia SAP Ariba"
            onClick={() => onSelect('status')}
          />

          <FeatureCard
            icon={<ShieldCheck />}
            title="Validación XML SUNAT"
            description="Valida XML según normativa SUNAT"
            onClick={() => onSelect('validacion')}
          />

          <FeatureCard
            icon={<FileSearch />}
            title="Buscar Facturas"
            description="Consulta facturas enviadas a Ariba (Invoice Extraction)"
            onClick={() => onSelect('facturas')}
          />

          <FeatureCard
            icon={<GitCompare />}
            title="Comparar XML"
            description="Validar campos XML"
            onClick={() => onSelect('compXML')}
          />

          <FeatureCard
            icon={<FlaskConical />}
            title="Casos de Prueba"
            description="Escenarios y datos de prueba por canal"
            onClick={() => onSelect('pruebas')}
          />

          <FeatureCard
            icon={<FileCode2 />}
            title="Especificaciones Técnicas"
            description="Documentos entre el área Funcional y Técnica"
            onClick={() => onSelect('especificaciones')}
          />

          {FLOW_EXTENSION_API_HABILITADO && (
            <FeatureCard
              icon={<Cpu />}
              title="Flow Extension API"
              description="Extiende flujos estándar de Ariba"
              onClick={() => onSelect('flow')}
            />
          )}

        </div>
      </div>

      {/* Guía de Integración Estándar — mismo criterio que SolucionPlaceholder.tsx
          (no renderiza nada si el Admin no cargó una guía para esta combinación). */}
      {projectId && <GuiaIntegracionCard projectId={projectId} solucion="commerce" />}

      {/* Orden estandarizado en todas las soluciones: Header → Integración
          (Configuraciones Generales) → Canales de Integración del módulo
          → Recursos Útiles (siempre al final). */}
      {projectId && <IntegrationSection projectId={projectId} />}

      {/* 🔌 Canales de Integración específicos de Commerce (flujo P2P,
          modelo ABAP/IDoc clásico) — SOLO On-Premise. "Configuraciones
          Generales" ya no vive acá, se muestra arriba vía IntegrationSection. */}
      {!esCloud && (
        <div>
          <h3 className="text-xl mb-4 font-medium text-green-200">Canales de Integración</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FeatureCard
              icon={<FileText />}
              title="Orden de Compra"
              description="Configuraciones de la OC"
              onClick={() => onSelect('integ-oc')}
              progress={channelsProgress['purchaseOrder']}
              progressLoading={progressLoading}
              testProgress={casosPruebaProgress['oc']}
              testProgressLoading={testProgressLoading}
            />

            <FeatureCard
              icon={<Truck />}
              title="Aviso de Expedición"
              description="Envío de Aviso de Expedición"
              onClick={() => onSelect('integ-expedicion')}
              progress={channelsProgress['asn']}
              progressLoading={progressLoading}
              testProgress={casosPruebaProgress['expedicion']}
              testProgressLoading={testProgressLoading}
            />

            <FeatureCard
              icon={<FileCheck />}
              title="Confirmación"
              description="Confirmación de la Orden de Compra"
              onClick={() => onSelect('integ-confirmacion')}
              progress={channelsProgress['orderConfirmation']}
              progressLoading={progressLoading}
              testProgress={casosPruebaProgress['confirmacion']}
              testProgressLoading={testProgressLoading}
            />

            <FeatureCard
              icon={<PackageCheck />}
              title="Recepción de Mercancía"
              description="Configuración de Recepción de Mercancía"
              onClick={() => onSelect('integ-recepcion')}
              progress={channelsProgress['goodsReceipt']}
              progressLoading={progressLoading}
              testProgress={casosPruebaProgress['recepcion']}
              testProgressLoading={testProgressLoading}
            />

            <FeatureCard
              icon={<ClipboardList />}
              title="HES"
              description="HES creadas desde Ariba"
              onClick={() => onSelect('integ-hes-ariba')}
              progress={channelsProgress['serviceEntrySheet']}
              progressLoading={progressLoading}
              testProgress={casosPruebaProgress['hes-ariba']}
              testProgressLoading={testProgressLoading}
            />

            <FeatureCard
              icon={<Database />}
              title="HES - ERP"
              description="HES creadas desde ERP"
              onClick={() => onSelect('integ-hes-erp')}
              progress={channelsProgress['serviceEntrySheetResponse']}
              progressLoading={progressLoading}
              testProgress={casosPruebaProgress['hes-erp']}
              testProgressLoading={testProgressLoading}
            />

            <FeatureCard
              icon={<Coins />}
              title="Facturas"
              description="Facturas enviadas desde Ariba"
              onClick={() => onSelect('integ-facturas')}
              progress={channelsProgress['invoice']}
              progressLoading={progressLoading}
              testProgress={casosPruebaProgress['facturas']}
              testProgressLoading={testProgressLoading}
            />

            <FeatureCard
              icon={<Receipt />}
              title="Estatus de Facturas"
              description="Envío del estatus de facturas hacia Ariba (ISU)"
              onClick={() => onSelect('integ-estatus-facturas')}
              progress={channelsProgress['invoiceStatus']}
              progressLoading={progressLoading}
              testProgress={casosPruebaProgress['estatus-facturas']}
              testProgressLoading={testProgressLoading}
            />

          </div>
        </div>
      )}

      {/* Communication Arrangements (K7) — reemplaza a los canales ABAP de
          arriba en proyectos Cloud. */}
      {esCloud && projectId && <ArrangementsSection projectId={projectId} solucion="commerce" />}

      {/* 📚 Recursos */}
      <div>
        <h3 className="text-xl mb-3 font-medium text-gray-200">Recursos útiles</h3>

        <div className="space-y-2 text-blue-400">
          <a href="https://api.sap.com" target="_blank" rel="noreferrer" className="hover:underline block">
            SAP API Business Hub
          </a>
          <a href="https://help.sap.com" target="_blank" rel="noreferrer" className="hover:underline block">
            SAP Help Portal
          </a>
        </div>
      </div>

    </div>
  );
}

// 🎨 Card reusable — `progress`/`progressLoading` son opcionales: solo las
// cards de "Canales de Integración" los pasan, el resto de usos (Funcionalidades)
// queda igual que antes. `testProgress` (casos de prueba aprobados/total,
// ver useCasosPruebaProgress.ts) es un badge SEGUNDO e independiente del de
// configuración — a propósito no se fusionan (miden cosas distintas: "está
// configurado" vs "está probado"). Solo se muestra si ya hay al menos 1
// caso de prueba cargado para ese canal (total > 0) — no tiene sentido
// mostrar "0/0" en cada card antes de que exista ningún caso.
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  progress?: ChannelProgress;
  progressLoading?: boolean;
  testProgress?: CasoPruebaProgress;
  testProgressLoading?: boolean;
}

function FeatureCard({ icon, title, description, onClick, progress, progressLoading, testProgress, testProgressLoading }: FeatureCardProps) {
  const showProgress = progress !== undefined || progressLoading;
  const showTestProgress = testProgressLoading || (testProgress !== undefined && testProgress.total > 0);
  return (
    <div
      onClick={onClick}
      className="cursor-pointer bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-5 transition-all duration-200"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-blue-400 mb-2">{icon}</div>
        <div className="flex items-center gap-1.5">
          {showTestProgress && (
            testProgressLoading
              ? <div className="w-14 h-4 rounded-full bg-white/10 animate-pulse" />
              : testProgress && (
                <span className="flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap bg-purple-500/15 text-purple-300" title="Casos de prueba aprobados">
                  <FlaskConical className="w-3 h-3" /> {testProgress.aprobados}/{testProgress.total}
                </span>
              )
          )}
          {showProgress && (
            progressLoading
              ? <div className="w-10 h-4 rounded-full bg-white/10 animate-pulse" />
              : progress && <ChannelProgressBadge completed={progress.completed} total={progress.total} />
          )}
        </div>
      </div>
      <h4 className="text-lg font-semibold text-white">{title}</h4>
      <p className="text-sm text-gray-400 mt-1">{description}</p>
      {showProgress && (
        progressLoading ? (
          <div className="w-full h-1 rounded-full bg-white/10 mt-2 animate-pulse" />
        ) : progress && (
          <>
            <ChannelProgressBar completed={progress.completed} total={progress.total} />
            <p className="text-xs text-gray-500 mt-1">{progress.contextText}</p>
          </>
        )
      )}
    </div>
  );
}